import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const FAQSection = () => {
  const faqs = [
    {
      question: 'How long does plaster take to dry before I can paint?',
      answer: 'Drying times vary depending on the thickness of the plaster, room ventilation, and weather conditions. You\'ll see the colour change from dark to light pink/white as it dries. We recommend waiting until all darker patches have lightened before applying mist coat and paint. Rushing this can lead to peeling or bubbling paint.',
    },
    {
      question: 'Do I need to do anything to prepare the room?',
      answer: 'The more space the better. We ask that you remove small items and valuables from the room. We\'ll cover floors, furniture, and door frames with protective sheeting. For larger items like sofas, we can work around them if they\'re moved to the centre of the room. We\'ll discuss specific preparation needs at the quote stage.',
    },
    {
      question: 'Can you cover over artex ceilings?',
      answer: 'In most cases, yes. We can skim over artex to create a smooth modern finish. However, some older artex may contain asbestos, so if your home was built before 2000, we may recommend testing first. We\'ll advise during the site visit whether a skim-over or alternative approach is best for your ceiling.',
    },
    {
      question: 'How much notice do you need for bookings?',
      answer: 'Our lead time varies throughout the year. Typically we can schedule work within a couple of weeks, but during busy periods (spring and autumn especially) it may be longer. For urgent repairs, we try to accommodate where possible.',
    },
    {
      question: 'Do you provide quotes in writing?',
      answer: 'Absolutely. Every quote is provided in writing, clearly itemising the work to be done and the total cost. We don\'t have hidden fees or surprise extras. If we discover additional work is needed once we start, we\'ll discuss it with you before proceeding.',
    },
    {
      question: 'What areas of Northampton do you cover?',
      answer: 'We cover Northampton and the surrounding 30-mile radius, including Wellingborough, Kettering, Daventry, Towcester, Brackley, and Rushden. If you\'re slightly outside this area, get in touch anyway — we may still be able to help, especially for larger projects.',
    },
    {
      question: 'Do I need to be home while you work?',
      answer: 'Not necessarily. Many of our customers leave us a key and we get on with the work. We just need access to water, electricity, and the room itself. We\'re fully insured and happy to provide references.',
    },
    {
      question: 'How do you price your work — by the day or by the job?',
      answer: 'We price by the job, not by the day. This means your quote is fixed and won\'t change unless you ask for additional work or unforseeen works are required. We believe this is fairer for customers — you know exactly what you\'re paying, and we\'re motivated to work efficiently without cutting corners.',
    },
  ];

  return (
    <section 
      id="faq" 
      className="py-20 lg:py-28 bg-slate-50"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-red-600 font-semibold text-sm tracking-wider uppercase mb-4">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Manrope'] mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Common questions about our plastering services in Northampton
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-white rounded-xl border border-slate-200 px-6 shadow-sm data-[state=open]:shadow-md transition-shadow duration-200"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left py-5 hover:no-underline">
                <span className="font-semibold text-slate-900 font-['Manrope'] pr-4">
                  {faq.question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <p className="text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Additional Help */}
        <div className="text-center mt-12">
          <p className="text-slate-600">
            Have another question?{' '}
            <a 
              href="#contact" 
              className="text-red-600 hover:text-red-700 font-semibold"
            >
              Get in touch
            </a>
            {' '}and we'll be happy to help.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
